'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import Link from 'next/link';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { usePersonalityFitTracking } from '../hooks/usePersonalityFitTracking';

export default function WapPersonalityProductLists({ productLists }: { productLists: any[] }) {
  const currency = useStore((s) => s.currency);
  const userInfo = useStore((s) => s.userInfo);
  const { navigate } = useAppNavigation();
  const { trackMktClick, trackMktExp } = usePersonalityFitTracking();

  if (!Array.isArray(productLists) || productLists.length === 0) return null;

  // 单个商品项组件（含埋点）
  const ProductItem = ({ item, index }: { item: any; index: number }) => {
    const [expRef, inView] = useInView({ threshold: 0.5, triggerOnce: true });
    const [btnExpRef, btnInView] = useInView({ threshold: 0.5, triggerOnce: true });

    const href =
      Number(item?.jump_type) === 2
        ? `/goods-detail/${item?.jump_value}`
        : `/goods-list/${item?.jump_value}`;
    const hasId = Boolean(item?.id);
    const pInfo: any = priceInfo(userInfo, item, currency) || {};
    const nowPrice = formatPriceNumberWithSymbol(
      String(pInfo?.sell_price || pInfo?.price || '0'),
      currency,
    );
    const oldPrice =
      pInfo && pInfo.price && pInfo.sell_price && pInfo.sell_price !== pInfo.price
        ? formatPriceNumberWithSymbol(String(pInfo.price), currency)
        : '';

    // 构建埋点参数
    const buildTrackParams = (mktName: string) => {
      const params: any = { mkt_name: mktName };
      if (Number(item?.jump_type) === 2) {
        params.shop_sku = item?.shop_sku || '';
        params.business_sku = item?.business_sku || '';
        params.product_sku = item?.jump_value || '';
      } else if (Number(item?.jump_type) === 1) {
        params.group_id = item?.jump_value || '';
      }
      return params;
    };

    // 商品卡片曝光
    useEffect(() => {
      if (inView) {
        try {
          trackMktExp(buildTrackParams(`Product_${index}`));
        } catch {
          /* noop */
        }
      }
    }, [inView, index]);

    // 按钮曝光
    useEffect(() => {
      if (btnInView && item?.btn_word) {
        try {
          trackMktExp(buildTrackParams(`Product_${index}_Btn`));
        } catch {
          /* noop */
        }
      }
    }, [btnInView, index, item?.btn_word]);

    return (
      <li>
        <Link
          href={href}
          className="product-item block mt-[20px]"
          onClick={(e) => {
            e.preventDefault();
            // 列表页跳转需要标记 sessionStorage
            if (
              Number(item?.jump_type) === 1 &&
              typeof window !== 'undefined' &&
              window.sessionStorage
            ) {
              try {
                window.sessionStorage.setItem('zl_goodsListEnterPageScene', '活动专题页');
              } catch {
                /* noop */
              }
            }
            try {
              trackMktClick(buildTrackParams(`Product_${index}`));
            } catch {
              /* noop */
            }
            navigate(href);
          }}
        >
          <div
            ref={expRef}
            className="img-wrap relative"
            style={item?.aspectRatio ? { aspectRatio: String(item.aspectRatio) } : undefined}
          >
            <Image src={item?.image} alt="" width={711} height={851} className="w-full h-auto" />

            {item?.description && (
              <p
                className="word absolute text-white text-[12px] leading-[16px] text-center"
                style={{ width: '270px', top: '318px', right: '42px' }}
              >
                {item.description}
              </p>
            )}

            {hasId && (
              <div className="price notranslate absolute left-[15px] bottom-[28px]">
                <span className="now-price notranslate text-[18px] font-bb text-white leading-[18px]">
                  {nowPrice}
                </span>
                {oldPrice && (
                  <span className="old-price notranslate ml-[6px] text-[12px] text-white line-through">
                    {oldPrice}
                  </span>
                )}
              </div>
            )}

            {item?.btn_word && (
              <div
                ref={btnExpRef}
                className="btn absolute right-[15px] bottom-[20px] h-[33px] rounded-[17px] text-[12px] font-bb text-black"
                style={{
                  padding: '0 28px',
                  lineHeight: '36px',
                  backgroundColor: item?.btn_bg,
                  opacity: Number(item?.btn_opacity || 1),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  // 列表页跳转需要标记 sessionStorage
                  if (
                    Number(item?.jump_type) === 1 &&
                    typeof window !== 'undefined' &&
                    window.sessionStorage
                  ) {
                    try {
                      window.sessionStorage.setItem('zl_goodsListEnterPageScene', '活动专题页');
                    } catch {
                      /* noop */
                    }
                  }
                  try {
                    trackMktClick(buildTrackParams(`Product_${index}_Btn`));
                  } catch {
                    /* noop */
                  }
                  navigate(href);
                }}
              >
                {item?.btn_word}
              </div>
            )}
          </div>
        </Link>
      </li>
    );
  };

  return (
    <ul className="personality-goods-list">
      {productLists.map((item: any, index) => (
        <ProductItem key={index} item={item} index={index} />
      ))}
    </ul>
  );
}
